import React, { useEffect, useState } from "react";
import DialogWrapper from "./DialogWrapper";
import StyledSelect from "./elements/StyledSelect";
import { Grid } from "@mui/material";
import FileUpload from "./FileUpload";
import StyledButton from "./elements/StyledButton";
import { StyledBody, Title } from "./styled/ModelDialog.styled";
import StyledTextField from "./elements/StyledTextField";
import { modelSchema } from "../validation/ModelSchema";
import { joiResolver } from "@hookform/resolvers/joi";
import { useForm } from "react-hook-form";
import MultiSelectChip from "./elements/MultiSelectChip";
import { BiUpload } from "react-icons/bi";
import { toast } from "react-toastify";
import CountryTable from "./CountryTable";
import StyledTable from "./StyledTable";
import { SelectUserHead } from "../constants/Data";
import { getAllUsers } from "../services/api/users.api";
import * as XLSX from "xlsx";
import { uploadFile } from "../services/api/fileUpload";
import { getAllCountries } from "../services/api/country.api";

export default function ModelDialog({
  edit,
  open,
  setOpen,
  addNewTemplate,
  editTemplate,
  dataForEdit,
  reRender,
  setReRender,
  ...props
}) {
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(modelSchema),
  });

  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState(["Site Ref", "Longitude", "Latitude"]);
  const [fileUrl, setFileUrl] = useState();
  const [country, setCountry] = useState("init");
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedModelVersions, setSelectedModelVersions] = useState([]);
  const [countryVersions, setCountryVersions] = useState([]);
  const [order, setOrder] = useState({ id: 1, sort: "asc" });
  const [selectAll, setSelectAll] = useState(false);
  const [selectUserRows, setSelectUserRows] = useState([]);
  const [isCountryRemovingWarningOpen, setIsCountryRemovingWarningOpen] =
    useState(false);
  const [selectedCountryForDeletion, setSelectedCountryForDeletion] =
    useState(null);
  const [countries, setCountries] = useState([]);

  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const getCountries = async () => {
      try {
        let response = [];
        response = await getAllCountries();
        response.sort((a, b) => a.label.localeCompare(b.label));
        setCountries(response);
      } catch (err) {
        console.log(err);
        toast.error(err.message, { theme: "colored" });
      }
    };
    getCountries();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const getUsers = async () => {
      if (getValues("name")) {
        try {
          const userData = await getAllUsers(
            1,
            "name",
            "asc",
            "non_admin",
            20000
          );
          const formattedUserData = [];
          userData.docs.forEach((doc) => {
            let isAllocated = false;
            let tempName = getValues("name");
            doc.templates.forEach((template) => {
              if (tempName === template.name) {
                isAllocated = true;
              }
            });
            formattedUserData.push({
              id: doc._id,
              name: doc.name,
              user_role: doc.user_role,
              select: isAllocated,
            });
          });
          setSelectUserRows([...formattedUserData]);
        } catch (err) {
          console.log(err);
        }
      }
    };
    getUsers();
    // eslint-disable-next-line
  }, [getValues("name"), reRender, uploading]);

  const resetAll = () => {
    if (!edit) {
      setFile(null);
      setFileUrl(null);
      setCountry("init");
      setSelectedCountries([]);
      setSelectedModelVersions([]);
      setCountryVersions([]);
      setValue("name", null);
      setValue("countries", null);
      setValue("url", null);
      clearErrors();
    }
  };

  const resetAllInEditMode = () => {
    if (dataForEdit?.countries) {
      setValue("name", dataForEdit.name);
      setValue("countries", dataForEdit.countries);
      setValue("headers", dataForEdit.headers);
      setValue("url", dataForEdit.url);
      setFileUrl(dataForEdit.url);
      setFile();
      setHeaders(dataForEdit.headers);
      setCountryVersions(dataForEdit.countries);
      const countryArray = dataForEdit.countries.map((n) => {
        return n.country;
      });
      setSelectedCountries([...countryArray]);
      let availableVersions = [];
      dataForEdit.countries.forEach((coun) => {
        coun.versions.forEach((version) => {
          const check = availableVersions.indexOf(version);
          if (check < 0) {
            availableVersions.push(version);
          }
        });
      });
      setSelectedModelVersions([...availableVersions]);
    }
    setReRender(!reRender);
  };

  const getFileNameFromUrl = (url) => {
    if (url) {
      let arrayAfterSplit = url.split("/");
      if (arrayAfterSplit.length > 0) {
        return arrayAfterSplit[arrayAfterSplit.length - 1];
      }
    }
    return url;
  };

  useEffect(() => {
    if (dataForEdit?.countries) {
      setValue("name", dataForEdit.name);
      setValue("countries", dataForEdit.countries);
      setValue("headers", dataForEdit.headers);
      setValue("url", dataForEdit.url);
      setFileUrl(dataForEdit.url);
      setFile();
      setHeaders(dataForEdit.headers);
      setCountryVersions(dataForEdit.countries);
      const countryArray = dataForEdit.countries.map((n) => {
        return n.country;
      });
      setSelectedCountries([...countryArray]);
      let availableVersions = [];
      dataForEdit.countries.forEach((coun) => {
        coun.versions.forEach((version) => {
          const check = availableVersions.indexOf(version);
          if (check < 0) {
            availableVersions.push(version);
          }
        });
      });
      setSelectedModelVersions([...availableVersions]);
    }
  }, [dataForEdit, setValue]);

  useEffect(() => {
    if (errors.url || errors.countries || errors.name) {
      toast.error("Please enter all required fields and upload the file.", {
        theme: "colored",
      });
    }
  }, [errors]);

  useEffect(() => {
    setValue("created_by", user?.name);
  }, [user, setValue]);

  const hiddenFileInput = React.useRef(null);
  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  const onChangeFile = (event) => {
    const fileType = event.target.files[0].type;
    const [fileread] = event.target.files;
    const reader = new FileReader();
    if (
      fileType !== "application/vnd.ms-excel" &&
      fileType !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      toast.error("Invalid file type", { theme: "colored" });
      return;
    }
    setFile(event.target.files[0]);
    reader.onload = (event) => {
      const bstring = event.target.result;
      const wrb = XLSX.read(bstring, { type: "binary" });
      const wsname = wrb.SheetNames[0];
      const wrSheet = wrb.Sheets[wsname];
      const csvData = XLSX.utils.sheet_to_csv(wrSheet, { header: 1 });
      let dataArr = csvData.split(/\r?\n/);
      const headStr = dataArr[0];
      var headArr = headStr.split(",");
      let formattedHeadArr = [];
      headArr.forEach((item) => {
        if (item !== "" && item !== null) {formattedHeadArr.push(item);}
      });
      setHeaders(formattedHeadArr);
      setValue("headers", formattedHeadArr);
    };
    reader.readAsBinaryString(fileread);
    setFileUrl();
  };

  const allCheck = () => {
    if (selectAll) {
      const checkAll = selectUserRows.map((data) => {
        return {
          id: data.id,
          name: data.name,
          user_role: data.user_role,
          select: false,
        };
      });
      setSelectUserRows([...checkAll]);
      setSelectAll(false);
      return;
    }
    if (!selectAll) {
      const checkAll = selectUserRows.map((data) => {
        return {
          id: data.id,
          name: data.name,
          user_role: data.user_role,
          select: true,
        };
      });
      setSelectUserRows([...checkAll]);
      setSelectAll(true);
    }
  };

  const handleUploadDocument = async (event) => {
    try {
      if (getValues("name") === null) {
        toast.warning("Please name the module", {
          theme: "colored",
        });
        return;
      }
      if (selectedCountries.length < 1) {
        toast.warning("Please add countries", {
          theme: "colored",
        });
        return;
      }
      if (selectedModelVersions.length < 1) {
        toast.warning("Please add versions", {
          theme: "colored",
        });
        return;
      }
      if (!file) {
        toast.warning("Please choose a document", { theme: "colored" });
        return;
      }
      setUploading(true);
      const uploadName = Date.now() + "-" + file.name;
      const templateLocation = encodeURI("Template" + "/" + uploadName);
      await uploadFile({
        name: templateLocation,
        file: file,
        type: file.type,
      });
      setUploading(false);
      if (!edit && countryVersions.length < 1) {
        const versions = selectedCountries.map((nc) => {
          return { country: nc, versions: selectedModelVersions };
        });
        setCountryVersions([...versions]);
      }
      setFileUrl(decodeURI(templateLocation));
      setValue("url", decodeURI(templateLocation));
    } catch (err) {}
  };

  const addVersions = (event) => {
    if (event.key === "Enter") {
      const available = selectedModelVersions?.find(
        (element) => element === event.target.value
      );
      if (available) {
        toast.warning("Module Version is already assigned", {
          theme: "colored",
        });
        return;
      }
      if (selectedModelVersions) {
        const newModels = [...selectedModelVersions, event.target.value];
        let updatedCountryVersions = countryVersions.map((item) => {
          return {
            ...item,
            versions: [...item.versions, event.target.value],
          };
        });
        setCountryVersions([...updatedCountryVersions]);
        setSelectedModelVersions([...newModels]);
        return;
      }
      setSelectedModelVersions([event.target.value]);
    }
  };

  const handleDeleteCountryVersions = (e) => {
    const newVersionCountry = countryVersions.map((data) => {
      if (data.country === e.country) {
        const newModels = data.versions.filter((items) => {
          return items !== e.version;
        });
        return { country: data.country, versions: newModels };
      }
      return data;
    });
    setCountryVersions([...newVersionCountry]);
  };

  const handleDeleteModelVersions = (e) => {
    const newModels = selectedModelVersions.filter((items) => {
      return items !== e;
    });
    setSelectedModelVersions([...newModels]);
    let updatedCountryVersions = countryVersions.map((countryVersion) => {
      let updatedVersions = countryVersion.versions.filter((item) => {
        return item !== e;
      });
      return {
        ...countryVersion,
        versions: [...updatedVersions],
      };
    });
    setCountryVersions([...updatedCountryVersions]);
  };

  const OnClickCountryChipCloseIcon = (e) => {
    setSelectedCountryForDeletion(e);
    setIsCountryRemovingWarningOpen(true);
  };

  const handleDeleteCountriesAfterWarning = () => {
    const newCountries = selectedCountries.filter((items) => {
      return items !== selectedCountryForDeletion;
    });
    const newCountryVersions = countryVersions.filter(
      ({ country }) => !country.includes(selectedCountryForDeletion)
    );
    setCountryVersions([...newCountryVersions]);
    setSelectedCountries([...newCountries]);
  };

  const handleDeleteCountries = (e) => {
    const newCountries = selectedCountries.filter((items) => {
      return items !== e;
    });
    const newCountryVersions = countryVersions.filter(
      ({ country }) => !country.includes(e)
    );
    setCountryVersions([...newCountryVersions]);
    setSelectedCountries([...newCountries]);
  };

  const assignCountries = (e) => {
    if (e === "all") {
      if (edit) {
        const editNewCountry = countries.map(({ value }) => {
          return value;
        });
        setSelectedCountries([...editNewCountry]);
        let newlyAddedCountries = [];
        countries.forEach(({ value }) => {
          let isExists = false;
          countryVersions.forEach((item) => {
            if (item.country === value) {
              isExists = true;
            }
          });
          if (!isExists) {
            newlyAddedCountries.push({ country: value, versions: [] });
          }
        });
        setCountryVersions([...countryVersions, ...newlyAddedCountries]);
        setCountry("init");
      }
      const newCountries = countries.map(({ value }) => {
        return value;
      });
      setSelectedCountries([...newCountries]);
      setCountry("init");
    } else {
      const available = selectedCountries.find((element) => element === e);
      if (available) {
        toast.warning("Country is already assigned", { theme: "colored" });
        setCountry("init");
        return;
      }
      if (edit) {
        const editNewCountry = [...selectedCountries, e];
        setSelectedCountries([...editNewCountry]);
        const newVersionCountryAdd = { country: e, versions: [] };
        setCountryVersions([...countryVersions, newVersionCountryAdd]);
        setCountry("init");
      }
      const newCountries = [...selectedCountries, e];
      setSelectedCountries([...newCountries]);
      setCountry("init");
    }
  };

  const onClickUser = async (e) => {
    let updatedSelectedUsers = selectUserRows.map((item) => {
      if (item.id === e.id) {
        return { ...item, select: e.select };
      }
      return item;
    });
    setSelectUserRows([...updatedSelectedUsers]);
  };

  const assignVersionCountry = (e) => {
    const selectedData = countryVersions.find(
      (object) => object.country === e.country
    );
    const currentModels = selectedData.versions;
    const alreadyAssigned = currentModels.find(
      (object) => object === e.version
    );
    if (alreadyAssigned) {
      toast.warning("Module Already Assigned");
      return;
    }
    const newVersionCountry = countryVersions.map((data) => {
      if (data.country === e.country) {
        return {
          country: data.country,
          versions: [...data.versions, e.version],
        };
      }
      return data;
    });
    setCountryVersions([...newVersionCountry]);
  };

  const isValidVersionAssignedForEachCountry = (countries) => {
    let isAssigned = true;
    countries.forEach((country) => {
      if (country.versions.length < 1) {
        isAssigned = false;
      }
    });
    return isAssigned;
  };

  const submitUserInformation = (e) => {
    const userArray = [];
    selectUserRows.forEach((row) => {
      if (row.select) userArray.push(row.id);
    });
    e.users = userArray;
    if (
      selectedModelVersions.length > 0 &&
      isValidVersionAssignedForEachCountry(e.countries)
    ) {
      if (fileUrl) {
        setOpen(false);
        if (edit) {
          editTemplate({ ...e, usersObjects: selectUserRows });
          return;
        }
        resetAll();
        addNewTemplate(e);
      } else {
        toast.warning("Upload template file before creating a module.", {
          theme: "colored",
        });
        return;
      }
    } else {
      toast.warning(
        "Version is required. Please assign version(s) to countries",
        {
          theme: "colored",
        }
      );
      return;
    }
  };

  const cancelAction = () => {
    if (edit) {
      resetAllInEditMode();
    } else {
      resetAll();
    }
    setOpen(false);
  };

  const onDeselectFile = async () => {
    setFile(null);
    hiddenFileInput.current.value = "";
    setFileUrl(null);
  };

  return (
    <DialogWrapper
      title={edit ? "Save" : "Add New Module"}
      cancelLabel="Cancel"
      actionLabel={edit ? "Save" : "Add New Module"}
      open={open}
      setOpenModal={setOpen}
      acceptButton={() => {
        setValue("countries", countryVersions);
        handleSubmit(submitUserInformation)();
        if (edit) {
          resetAll();
        } else {
          clearErrors();
        }
      }}
      cancelAction={cancelAction}
      onClose={() => {
        cancelAction();
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={3}
      >
        <Grid item xs={12} md={6} lg={4}>
          <StyledTextField
            id="model_name"
            label="Module Name *"
            disabled={edit ? true : false}
            placeholder="Input Module Name"
            inputProps={{ ...register("name") }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          {edit ? (
            <>
              <StyledSelect
                label={"Country *"}
                defaultVal={"Select the Countries"}
                options={[{ label: "All", value: "all" }, ...countries]}
                control={country}
                setControl={assignCountries}
              />
              <MultiSelectChip
                models={selectedCountries}
                handleDelete={OnClickCountryChipCloseIcon}
              />
            </>
          ) : (
            <>
              {!fileUrl && (
                <>
                  <StyledSelect
                    label={"Country *"}
                    defaultVal={"Select the Countries"}
                    options={[{ label: "All", value: "all" }, ...countries]}
                    control={country}
                    setControl={assignCountries}
                  />
                  <MultiSelectChip
                    models={selectedCountries}
                    handleDelete={handleDeleteCountries}
                  />
                </>
              )}
            </>
          )}
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          {edit ? (
            <>
              <StyledTextField
                id="versions"
                label="Module Versions *"
                placeholder="Input Module Versions"
                onKeyPress={addVersions}
              />
              <MultiSelectChip
                models={selectedModelVersions}
                handleDelete={handleDeleteModelVersions}
              />
            </>
          ) : (
            <>
              {!fileUrl && (
                <>
                  <StyledTextField
                    id="versions"
                    label="Module Versions *"
                    placeholder="Input Module Versions"
                    onKeyPress={addVersions}
                  />
                  <MultiSelectChip
                    models={selectedModelVersions}
                    handleDelete={handleDeleteModelVersions}
                  />
                </>
              )}
            </>
          )}
        </Grid>
        <Grid item xs={12} lg={12}>
          <FileUpload
            title="Upload the Module Template *"
            file={file}
            fileUrl={getFileNameFromUrl(fileUrl)}
            clickUpload={handleClick}
            onChangeFile={onChangeFile}
            hiddenFileInput={hiddenFileInput}
            onDeselectFile={onDeselectFile}
          />
          {(file || fileUrl) && (
            <StyledBody>
              Template file headers are{" "}
              {headers.map((header, i) => {
                if (i + 1 === headers.length)
                  return <b key={i}>{`'${header}' `}</b>;
                return <b key={i}>{`'${header}', `}</b>;
              })}
            </StyledBody>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledButton
            variant="outlined"
            iconLeft={<BiUpload />}
            loading={uploading}
            onClick={handleUploadDocument}
            disabled={fileUrl ? true : false}
          >
            {!fileUrl ? "Upload Document" : "File Uploaded"}
          </StyledButton>
        </Grid>
        <Grid item xs={12}>
          {fileUrl && (
            <>
              <Title>Add Module Version to Country</Title>
              <CountryTable
                rows={countryVersions}
                handleDeleteCountryVersions={handleDeleteCountryVersions}
                assignVersion={assignVersionCountry}
                selectedModelVersions={selectedModelVersions}
              />
              <Title>Select Users</Title>
              <StyledTable
                headCells={SelectUserHead}
                rows={selectUserRows}
                select
                order={order}
                setOrder={setOrder}
                onClickUser={onClickUser}
                allCheck={allCheck}
                selectAll={selectAll}
                loading={false}
              />
            </>
          )}
        </Grid>
      </Grid>
      <DialogWrapper
        title={"Warning"}
        cancelLabel="No"
        actionLabel={"Yes"}
        open={isCountryRemovingWarningOpen}
        setOpenModal={setIsCountryRemovingWarningOpen}
        acceptButton={() => {
          handleDeleteCountriesAfterWarning();
          setIsCountryRemovingWarningOpen(false);
        }}
        cancelAction={() => {
          setIsCountryRemovingWarningOpen(false);
        }}
        onClose={() => {
          setIsCountryRemovingWarningOpen(false);
        }}
      >
        If the country is removed all the versions will be removed as well. Do
        you like to continue?
      </DialogWrapper>
    </DialogWrapper>
  );
}
